// screens/RadarHome.tsx — R3b (plate 01/05): "Topics you are tracking" on
// the left, "Worth building — your call" on the right. R3b-2 (2026-09-27):
// `pythia_radar_topics` now returns each topic's FULL `last_run` object and
// `verdict_counts` in the one list call — the freshness line and the
// per-verdict counts both read straight off it, no second `topic_get` call
// needed for the home screen.
//
// R3d (2026-09-27, item 4 — "a settled proposal is never lost"): the right
// column gains a state strip (Waiting/Approved/Declined/All) and a search
// field over `pythia_radar_search`. A declined card offers Reconsider
// (reopen); an approved card names its to-do and offers Withdraw (which
// also drops the Core to-do it filed). The receipt card after Approve/Not
// now gains one Undo for that same decision — nothing here disappears, it
// moves to its state.
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  ApiError,
  approveRadarProposal,
  declineRadarProposal,
  getRadarNewsletter,
  listRadarProposals,
  listRadarTopics,
  reopenRadarProposal,
  searchRadar,
  withdrawRadarProposal,
  type RadarProposalApi,
  type RadarSearchHitApi,
  type RadarTopicListApi,
} from '../lib/api';
import { cadenceLine, nextRunLabel } from '../lib/radarCadence';
import { isResearching, topicFreshnessLine } from '../lib/radarFreshness';
import './RadarHome.css';

// R3b-3 (C1): the home polls while any topic's pending_command is
// queued/running — the same 30s cadence RadarTopics already uses for its
// own researching card, so the state clears from both surfaces together.
const RESEARCH_POLL_MS = 30_000;

// R3d (C4) — debounced so every keystroke doesn't fire a call.
const SEARCH_DEBOUNCE_MS = 300;

export interface RadarHomeProps {
  tenantId: string;
  onOpenTopic: (slug: string) => void;
  onOpenTopics: () => void;
  onOpenProposal: (id: string) => void;
  // R3e (C) — the newsletter link under the say-line.
  onOpenNewsletter: () => void;
}

type LoadState = { phase: 'loading' } | { phase: 'error'; message: string } | { phase: 'ready' };

// R3d (C4) — the state strip's own vocabulary; 'proposed' is the tool's own
// status word (the strip's label is "Waiting", the URL-free filter value is
// what the door already speaks).
type ProposalFilter = 'proposed' | 'approved' | 'declined' | 'all';

const FILTERS: ReadonlyArray<{ id: ProposalFilter; label: string }> = [
  { id: 'proposed', label: 'Waiting' },
  { id: 'approved', label: 'Approved' },
  { id: 'declined', label: 'Declined' },
  { id: 'all', label: 'All' },
];

const VERDICT_ORDER = ['adopt', 'adapt', 'watch', 'skip'] as const;

/** The topic card's second line — per-verdict counts since last viewed
 *  (R3b-2 item 2). Only the verdicts that actually moved are named.
 *
 *  Tunnel walk feedback: an all-zero count used to read "no verdicts yet"
 *  even for a topic that had already run — machine-voice honesty says a
 *  topic with a real run and nothing new since last looked is "nothing new
 *  since you looked", never phrased as if it had never run at all.
 *  "no verdicts yet" is kept for the one case that's actually true of: a
 *  topic with NO run at all (`lastRun` null — the field every run, discover
 *  or review, sets; lastDiscover/lastReview are narrower reads of the same
 *  history and can be null while a run still exists). */
function verdictLine(t: RadarTopicListApi): string {
  const parts = VERDICT_ORDER.filter((v) => t.verdictCounts[v] > 0).map((v) => `${t.verdictCounts[v]} ${v}`);
  if (parts.length > 0) return parts.join(' · ');
  return t.lastRun ? 'nothing new since you looked' : 'no verdicts yet';
}

function sayLine(topics: RadarTopicListApi[], waitingCount: number): string {
  if (waitingCount > 0) return `${waitingCount} proposal${waitingCount === 1 ? '' : 's'} wait${waitingCount === 1 ? 's' : ''}.`;
  // "Moving" — a topic with any non-zero verdict count since last viewed
  // (R3b-2 item 2), not merely a nonzero items-since-viewed count.
  const moving = topics.filter((t) => VERDICT_ORDER.some((v) => t.verdictCounts[v] > 0)).length;
  if (moving > 0) return `${moving} topic${moving === 1 ? ' is' : 's are'} moving.`;
  return 'Nothing waits for your decision.';
}

export function RadarHome({ tenantId, onOpenTopic, onOpenTopics, onOpenProposal, onOpenNewsletter }: RadarHomeProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [topics, setTopics] = useState<RadarTopicListApi[]>([]);
  const [proposals, setProposals] = useState<RadarProposalApi[]>([]);
  const [waitingCount, setWaitingCount] = useState(0);
  // R3e (C) — the newsletter link's own count, read once alongside the rest.
  const [newsletterCount, setNewsletterCount] = useState(0);
  const [filter, setFilter] = useState<ProposalFilter>('proposed');
  const [busy, setBusy] = useState<string | null>(null);
  const [receipts, setReceipts] = useState<Record<string, string>>({});
  // R3d (C4) — which action produced the standing receipt, so Undo knows
  // which of the two calls (withdraw/reopen) reverses it.
  const [decisionKind, setDecisionKind] = useState<Record<string, 'approved' | 'declined'>>({});
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<RadarSearchHitApi[] | null>(null);
  const [searching, setSearching] = useState(false);
  // R3g (2026-09-27): a list response that arrives after the person moved on
  // is dropped — the walk caught the Declined list overwriting Waiting (click
  // Reconsider, then the Waiting tab, before the reopen's own refetch lands:
  // the stale Declined-tab response must not clobber the Waiting tab that's
  // on screen by the time it arrives). One counter per independent list.
  const proposalsReqRef = useRef(0);
  const waitingReqRef = useRef(0);

  function loadFilteredProposals(f: ProposalFilter): Promise<RadarProposalApi[]> {
    return listRadarProposals(tenantId, f === 'all' ? {} : { status: f });
  }

  // Every caller that wants the visible proposals list refreshed goes
  // through here (refresh, switchFilter, the post-command refetch) — never
  // `setProposals(await loadFilteredProposals(f))` directly, or a late
  // response from a tab the person already left wins the race.
  async function loadProposalsGuarded(f: ProposalFilter): Promise<void> {
    const reqId = ++proposalsReqRef.current;
    const list = await loadFilteredProposals(f);
    if (proposalsReqRef.current === reqId) setProposals(list);
  }

  // R3g (C2, 2026-09-27) — the say-line's count is always the server's own
  // Waiting count, never arithmetic on the client's own state; the one
  // helper every one of the five decision actions (approve/decline/undo/
  // reconsider/withdraw) calls after it succeeds, so the count never lags.
  // Guarded the same way — a stale count response must not overwrite a
  // fresher one.
  async function refreshWaitingCount() {
    const reqId = ++waitingReqRef.current;
    const count = (await listRadarProposals(tenantId, { status: 'proposed' })).length;
    if (waitingReqRef.current === reqId) setWaitingCount(count);
  }

  async function refresh() {
    try {
      const reqId = ++proposalsReqRef.current;
      const [t, displayed, newsletter] = await Promise.all([
        listRadarTopics(tenantId),
        loadFilteredProposals(filter),
        getRadarNewsletter(tenantId).catch(() => null),
      ]);
      setTopics(t);
      const stillCurrent = proposalsReqRef.current === reqId;
      if (stillCurrent) setProposals(displayed);
      // A newsletter door that isn't reachable never blocks the rest of the
      // home screen — the link simply reads "nothing queued" until it is.
      if (newsletter) setNewsletterCount(newsletter.items.length);
      // The say-line's "N proposal(s) wait" always reads Waiting, whichever
      // tab is open — reuse the fetched list rather than a second call when
      // Waiting IS the open tab (only while that fetch is still the latest).
      if (filter === 'proposed') {
        if (stillCurrent) setWaitingCount(displayed.length);
      } else {
        await refreshWaitingCount();
      }
      setLoad({ phase: 'ready' });
    } catch (err) {
      setLoad({ phase: 'error', message: err instanceof ApiError ? err.message : 'the radar is not reachable' });
    }
  }

  useEffect(() => {
    setLoad({ phase: 'loading' });
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // R3b-3 (C1): the same 30s poll RadarTopics runs, so a topic tapped into
  // research from either surface clears on both once its pending_command
  // is gone — driven by pending_command, not a run's own kind (see
  // lib/radarFreshness's isResearching).
  useEffect(() => {
    if (load.phase !== 'ready') return;
    if (!topics.some((t) => isResearching(t.pendingCommand, t.lastRun))) return;
    const id = setInterval(() => void refresh(), RESEARCH_POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load.phase, topics]);

  // R3d (C4) — debounced search over proposals + items; an empty query
  // clears back to the state-strip view rather than showing "nothing found".
  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setHits(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      searchRadar(tenantId, q, 20)
        .then((r) => setHits(r))
        .catch(() => setHits([]))
        .finally(() => setSearching(false));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, tenantId]);

  async function switchFilter(f: ProposalFilter) {
    setFilter(f);
    try {
      await loadProposalsGuarded(f);
    } catch (err) {
      setLoad({ phase: 'error', message: err instanceof ApiError ? err.message : 'the radar is not reachable' });
    }
  }

  // A decided proposal stays ON THE CARD with a receipt in place of the
  // buttons — never a silent vanish (receipts, not acknowledgements). The
  // receipt text is Pythia's own, verbatim — never composed here (R3b-2
  // item 4).
  async function approve(p: RadarProposalApi) {
    setBusy(p.id);
    try {
      const r = await approveRadarProposal(tenantId, p.id);
      setReceipts((cur) => ({ ...cur, [p.id]: r.receipt ?? `filed as a to-do: "${r.task.title}"` }));
      setDecisionKind((cur) => ({ ...cur, [p.id]: 'approved' }));
      await refreshWaitingCount();
    } catch (err) {
      setReceipts((cur) => ({ ...cur, [p.id]: err instanceof ApiError ? err.message : 'not recorded' }));
    } finally {
      setBusy(null);
    }
  }

  async function decline(p: RadarProposalApi) {
    setBusy(p.id);
    try {
      const r = await declineRadarProposal(tenantId, p.id);
      setReceipts((cur) => ({ ...cur, [p.id]: r.receipt ?? 'kept under the topic; nothing filed' }));
      setDecisionKind((cur) => ({ ...cur, [p.id]: 'declined' }));
      await refreshWaitingCount();
    } catch (err) {
      setReceipts((cur) => ({ ...cur, [p.id]: err instanceof ApiError ? err.message : 'not recorded' }));
    } finally {
      setBusy(null);
    }
  }

  // R3d (C4) — Undo, on the receipt card: the SAME two calls the
  // Approved/Declined tabs' own Withdraw/Reconsider use, keyed off which
  // decision this card just made.
  async function undo(p: RadarProposalApi) {
    const kind = decisionKind[p.id];
    if (!kind) return;
    setBusy(p.id);
    try {
      const r = kind === 'approved' ? await withdrawRadarProposal(tenantId, p.id) : await reopenRadarProposal(tenantId, p.id);
      setReceipts((cur) => ({ ...cur, [p.id]: r.command.receipt ?? 'undone' }));
      setDecisionKind((cur) => {
        const next = { ...cur };
        delete next[p.id];
        return next;
      });
      // R3g (C1, 2026-09-27) — an undone card is back to its current server
      // status (proposed again); refetch the visible list so it shows THAT
      // status's buttons instead of sitting dead until the next full refresh.
      await loadProposalsGuarded(filter);
      await refreshWaitingCount();
    } catch (err) {
      setReceipts((cur) => ({ ...cur, [p.id]: err instanceof ApiError ? err.message : 'not recorded' }));
    } finally {
      setBusy(null);
    }
  }

  async function reconsider(p: RadarProposalApi) {
    setBusy(p.id);
    try {
      const r = await reopenRadarProposal(tenantId, p.id);
      setReceipts((cur) => ({ ...cur, [p.id]: r.command.receipt ?? 'reopened' }));
      // R3g (C1, 2026-09-27) — a reconsidered card is 'proposed' again on
      // the server; refetch the visible list so it shows THAT status's
      // buttons rather than the dead receipt-only card until a refresh.
      await loadProposalsGuarded(filter);
      await refreshWaitingCount();
    } catch (err) {
      setReceipts((cur) => ({ ...cur, [p.id]: err instanceof ApiError ? err.message : 'not recorded' }));
    } finally {
      setBusy(null);
    }
  }

  async function withdraw(p: RadarProposalApi) {
    setBusy(p.id);
    try {
      const r = await withdrawRadarProposal(tenantId, p.id);
      setReceipts((cur) => ({ ...cur, [p.id]: r.command.receipt ?? 'withdrawn' }));
      // R3g (C1, 2026-09-27) — a withdrawn card is 'proposed' again on the
      // server; refetch the visible list so it shows THAT status's buttons
      // rather than the dead receipt-only card until a refresh.
      await loadProposalsGuarded(filter);
      await refreshWaitingCount();
    } catch (err) {
      setReceipts((cur) => ({ ...cur, [p.id]: err instanceof ApiError ? err.message : 'not recorded' }));
    } finally {
      setBusy(null);
    }
  }

  const lastFinished = topics
    .map((t) => t.lastRun?.finishedAt)
    .filter((x): x is string => !!x)
    .sort()
    .at(-1);

  // C2 (R3b-3): sectioned by state — tracking under its own heading, a
  // topic researched once (never added to tracking) under its own; nothing
  // archived reaches this screen at all (pythia_radar_topics leaves an
  // archived topic out unless include_archived, which this call never
  // passes).
  const tracking = topics.filter((t) => t.state === 'tracking');
  const researchedOnce = topics.filter((t) => t.state === 'once');

  function topicCard(t: RadarTopicListApi) {
    const researching = isResearching(t.pendingCommand, t.lastRun);
    return (
      <button type="button" class="radar__card" key={t.slug} onClick={() => onOpenTopic(t.slug)}>
        <span class="radar__card-top">
          <span class="radar__card-name">{t.name}</span>
          {t.newSince > 0 ? <span class="radar__chip radar__chip--accent">{t.newSince} new</span> : null}
        </span>
        <span class="radar__card-line">
          {verdictLine(t)} · {t.proposalsProposed} proposal{t.proposalsProposed === 1 ? '' : 's'}
        </span>
        <span class="radar__card-sub">
          {cadenceLine(t.state, t.cadence)} · {nextRunLabel(t.nextDueAt)}
        </span>
        <span class="radar__card-sub">
          {researching
            ? `researching · ${t.pendingCommand?.status ?? 'queued'}${
                t.pendingCommand?.startedAt
                  ? ` · started ${new Date(t.pendingCommand.startedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
                  : ''
              }`
            : topicFreshnessLine(t.lastDiscover, t.lastRun)}
        </span>
      </button>
    );
  }

  // R3d (C4) — one proposal card, branching on its status (or on a
  // just-made session decision, which always wins over the fetched status
  // so the receipt + Undo show immediately without a refetch).
  function proposalCard(p: RadarProposalApi) {
    const receipt = receipts[p.id];
    const kind = decisionKind[p.id];
    // A just-made approve/decline keeps its present behaviour: receipt +
    // Undo REPLACES the buttons (R3d C4 design) — `decisionKind` is only
    // ever set by approve()/decline(), and cleared the moment undo() runs.
    if (receipt && kind) {
      return (
        <div class="radar__proposal" key={p.id}>
          <div class="radar__proposal-title">{p.title}</div>
          <div class="radar__receipt">{receipt}</div>
          <div class="radar__proposal-acts">
            <button type="button" class="radar__act" disabled={busy === p.id} onClick={() => void undo(p)}>
              Undo
            </button>
          </div>
        </div>
      );
    }
    // R3g (C1, 2026-09-27) — any other standing receipt (reconsider,
    // withdraw, or an undo that just cleared decisionKind) sits ABOVE the
    // actions that fit the proposal's CURRENT status, never in place of
    // them — a reconsidered/withdrawn card must not go button-less until a
    // refetch of its own (the final walk's own nit). The receipt text is
    // still Pythia's own, verbatim.
    const receiptLine = receipt ? <div class="radar__receipt">{receipt}</div> : null;
    if (p.status === 'approved') {
      return (
        <div class="radar__proposal" key={p.id}>
          <div class="radar__proposal-title">{p.title}</div>
          {receiptLine}
          <div class="radar__card-sub">approved{p.coreTaskRef ? ` · to-do ${p.coreTaskRef}` : ''}</div>
          <div class="radar__proposal-acts">
            <button type="button" class="radar__act" disabled={busy === p.id} onClick={() => void withdraw(p)}>
              Withdraw
            </button>
          </div>
        </div>
      );
    }
    if (p.status === 'declined') {
      return (
        <div class="radar__proposal" key={p.id}>
          <div class="radar__proposal-title">{p.title}</div>
          {receiptLine}
          <div class="radar__card-sub">declined</div>
          <div class="radar__proposal-acts">
            <button type="button" class="radar__act" disabled={busy === p.id} onClick={() => void reconsider(p)}>
              Reconsider
            </button>
          </div>
        </div>
      );
    }
    // 'proposed' — Waiting. The original approve/decline flow, plus the
    // "raised by you" chip (R3d item 5) when Huan raised it from an item.
    return (
      <div class="radar__proposal" key={p.id}>
        <div class="radar__proposal-title">
          {p.title}
          {p.raisedBy === 'huan' ? <span class="radar__chip" style={{ marginLeft: '6px' }}>raised by you</span> : null}
        </div>
        {receiptLine}
        <div class="radar__proposal-acts">
          <button type="button" class="radar__act radar__act--primary" disabled={busy === p.id} onClick={() => void approve(p)}>
            Approve
          </button>
          <button type="button" class="radar__act" disabled={busy === p.id} onClick={() => void decline(p)}>
            Not now
          </button>
          <button type="button" class="radar__act" onClick={() => onOpenProposal(p.id)}>
            Read the background
          </button>
        </div>
      </div>
    );
  }

  function searchHitCard(h: RadarSearchHitApi) {
    return (
      <button
        type="button"
        class="radar__card"
        key={`${h.kind}-${h.id}`}
        onClick={() => (h.kind === 'proposal' ? onOpenProposal(h.id) : h.topicSlug && onOpenTopic(h.topicSlug))}
      >
        <span class="radar__card-top">
          <span class="radar__card-name">{h.title}</span>
          <span class="radar__chip">{h.kind}</span>
        </span>
        <span class="radar__card-sub">{h.status ?? h.verdict ?? ''}</span>
      </button>
    );
  }

  return (
    <div class="radar">
      <div class="radar__eyebrow">Radar</div>

      {load.phase === 'loading' ? (
        <div class="radar__skeleton" aria-hidden="true">
          <div class="radar__skeleton-row" />
          <div class="radar__skeleton-row" />
        </div>
      ) : load.phase === 'error' ? (
        <div class="radar__say">
          The radar is not reachable.
          <br />
          <span class="radar__say-soft">{load.message}</span>
        </div>
      ) : (
        <>
          <div class="radar__say">{sayLine(topics, waitingCount)}</div>
          {/* R3e (C) — linked from the home, under the say-line (spec). */}
          <button type="button" class="radar__link radar__newsletter-link" onClick={onOpenNewsletter}>
            {newsletterCount > 0 ? `Newsletter · ${newsletterCount} queued` : 'Newsletter · nothing queued'}
          </button>
          <div class="radar__cols">
            <section class="radar__col">
              <div class="radar__col-title">
                <span>Topics you are tracking</span>
                <button type="button" class="radar__link" onClick={onOpenTopics}>
                  all topics
                </button>
              </div>
              {tracking.length === 0 ? (
                <div class="radar__empty">No topics yet. Add one from the topics window.</div>
              ) : (
                tracking.map((t) => topicCard(t))
              )}
            </section>

            <section class="radar__col">
              <div class="radar__col-title">
                <span>Worth building — your call</span>
              </div>
              <input
                type="search"
                class="radar__search"
                placeholder="search proposals and items"
                value={query}
                onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
              />
              {hits !== null ? (
                searching ? (
                  <div class="radar__empty">Searching…</div>
                ) : hits.length === 0 ? (
                  <div class="radar__empty">Nothing found.</div>
                ) : (
                  hits.map((h) => searchHitCard(h))
                )
              ) : (
                <>
                  <div class="radar__strip">
                    {FILTERS.map((f) => (
                      <button
                        type="button"
                        key={f.id}
                        class={`radar__strip-btn${filter === f.id ? ' radar__strip-btn--active' : ''}`}
                        onClick={() => void switchFilter(f.id)}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                  {proposals.length === 0 ? (
                    <div class="radar__empty">Nothing waits here.</div>
                  ) : (
                    proposals.map((p) => proposalCard(p))
                  )}
                </>
              )}
            </section>
          </div>

          {researchedOnce.length > 0 ? (
            <section class="radar__col" style={{ marginTop: '14px' }}>
              <div class="radar__col-title">
                <span>Researched once</span>
              </div>
              {researchedOnce.map((t) => topicCard(t))}
            </section>
          ) : null}

          <div class="radar__footer">
            Radar checks each topic on its own cadence, Toronto time.
            {lastFinished
              ? ` Last review finished ${new Date(lastFinished).toLocaleString([], {
                  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                })}.`
              : ' No review has finished yet.'}
          </div>
        </>
      )}
    </div>
  );
}
